// Formatted-document exports (Word .docx and PDF) that mirror the on-page
// reference list: selected citation style, hanging indent, double spacing,
// italics, hyperlinks/DOIs and display order.
import {
  Document, Packer, Paragraph, TextRun, ExternalHyperlink,
  convertInchesToTwip, LineRuleType, AlignmentType,
} from "docx";
import { jsPDF } from "jspdf";
import { orderReferences, yearSuffixMap, isNumbered, formatReferenceRuns } from "@/lib/citations";

function marker(style, i) {
  return style === "IEEE" ? `[${i + 1}] ` : `${i + 1}. `;
}

// Ordered entries: [{ runs: [{ text, italic, link? }] }] — number prefix included.
function buildEntries(refs, style) {
  const ordered = orderReferences(refs, style);
  const sfx = yearSuffixMap(refs);
  const numbered = isNumbered(style);
  return ordered.map((r, i) => {
    const runs = formatReferenceRuns(r, style, sfx.get(r));
    if (numbered) runs.unshift({ text: marker(style, i), italic: false });
    return { runs };
  });
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---- Word (.docx) ------------------------------------------------------------
export async function downloadDocx(refs, style, title, filenameBase) {
  const entries = buildEntries(refs, style);
  const hang = convertInchesToTwip(0.5);

  const refParagraphs = entries.map((e) =>
    new Paragraph({
      spacing: { line: 480, lineRule: LineRuleType.AUTO }, // double spacing
      indent: { left: hang, hanging: hang },               // 0.5in hanging indent
      children: e.runs.map((run) =>
        run.link
          ? new ExternalHyperlink({
              link: run.link,
              children: [new TextRun({ text: run.text, italics: run.italic, color: "0563C1", underline: {} })],
            })
          : new TextRun({ text: run.text, italics: run.italic }),
      ),
    }),
  );

  const doc = new Document({
    styles: { default: { document: { run: { font: "Times New Roman", size: 24 } } } },
    sections: [{
      properties: {},
      children: [
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { line: 480, lineRule: LineRuleType.AUTO, after: 240 },
          children: [new TextRun({ text: title || "References", bold: true, size: 28 })],
        }),
        ...refParagraphs,
      ],
    }],
  });

  const blob = await Packer.toBlob(doc);
  saveBlob(blob, `${filenameBase || "references"}.docx`);
}

// ---- PDF ---------------------------------------------------------------------
export function downloadPdf(refs, style, title, filenameBase) {
  const entries = buildEntries(refs, style);
  const M = 72, HANG = 36, SIZE = 12, LINE = 24;          // 1in margins, 0.5in hang, double space
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const rightEdge = pageW - M;
  let y = M;

  const setF = (bold, italic) =>
    doc.setFont("times", bold ? "bold" : italic ? "italic" : "normal");

  // Title
  setF(true, false);
  doc.setFontSize(14);
  const titleText = title || "References";
  const titleW = doc.getTextWidth(titleText);
  doc.text(titleText, (pageW - titleW) / 2, y);
  y += LINE * 1.4;
  doc.setFontSize(SIZE);

  const layoutEntry = (runs) => {
    const tokens = [];
    runs.forEach((r) => {
      r.text.split(/(\s+)/).forEach((seg) => {
        if (seg === "") return;
        tokens.push({ text: seg, italic: r.italic, link: r.link, space: /^\s+$/.test(seg) });
      });
    });
    let lineIndex = 0;
    const lineStart = () => (lineIndex === 0 ? M : M + HANG);
    let x = lineStart();
    const ensurePage = () => { if (y > pageH - M) { doc.addPage(); y = M; } };
    const newLine = () => { y += LINE; lineIndex++; x = lineStart(); ensurePage(); };
    ensurePage();

    for (const t of tokens) {
      setF(false, t.italic);
      const w = doc.getTextWidth(t.text);
      if (t.space && x === lineStart()) continue; // drop space at line start
      const avail = rightEdge - lineStart();
      if (!t.space && w > avail) {
        // token longer than a full line — break by characters
        let chunk = "";
        for (const ch of t.text) {
          const cw = doc.getTextWidth(chunk + ch);
          if (x + cw > rightEdge && chunk) {
            doc.text(chunk, x, y);
            if (t.link) doc.link(x, y - SIZE, doc.getTextWidth(chunk), SIZE, { url: t.link });
            newLine(); setF(false, t.italic);
            chunk = ch;
          } else {
            chunk += ch;
          }
        }
        if (chunk) {
          doc.text(chunk, x, y);
          if (t.link) doc.link(x, y - SIZE, doc.getTextWidth(chunk), SIZE, { url: t.link });
          x += doc.getTextWidth(chunk);
        }
        continue;
      }
      if (!t.space && x + w > rightEdge && x > lineStart()) { newLine(); setF(false, t.italic); }
      if (t.space && x === lineStart()) continue;
      doc.text(t.text, x, y);
      if (t.link && !t.space) doc.link(x, y - SIZE, w, SIZE, { url: t.link });
      x += w;
    }
    y += LINE; // paragraph break -> next entry starts on a new line
    if (y > pageH - M) { doc.addPage(); y = M; }
  };

  entries.forEach((e) => layoutEntry(e.runs));
  doc.save(`${filenameBase || "references"}.pdf`);
}
